import type { ICartEdge } from "@repo/shopify/src/queries/cart-gql";
import type { IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import { getDiscountName, getDiscountPriceInfo } from "@repo/shopify/src/utils/order";
import { transformWon } from "@repo/utils/src/number";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Button from "@components/atoms/Button";
import CompareAtPrice from "@components/atoms/CompareAtPrice";
import Icon from "@components/atoms/Icon";
import Image from "@components/atoms/Image";

import Stepper from "@molecules/Stepper";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { IconName } from "@const/enum";
import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";

function CartLineItem({ lineNode }: { lineNode: ICartEdge["node"] }) {
  const { t } = useTranslation();
  const { closeCart, openToast } = useLayout();
  const { cart, updateCartLoading, updateCartLineBase, removeCartLineBase } = useCart();
  const { customer } = useUser();
  const size = useMemo(() => lineNode.merchandise.selectedOptions.find((item) => item.name === "Size"), [lineNode]);

  // 직원 / 지인 인지 판단
  const isEmployee = useMemo(
    () => (customer ? customer.tags.includes("Employee") || customer.tags.includes("FRIENDS") : false),
    [customer],
  );

  // 정상가
  const compareAtPrice = useMemo(
    () => parseInt(lineNode.cost.compareAtAmountPerQuantity?.amount || "0") * lineNode.quantity,
    [lineNode],
  );

  // 할인가
  const price = useMemo(
    () =>
      Math.min(
        parseInt(lineNode.cost.amountPerQuantity.amount || "0") * lineNode.quantity,
        parseInt(lineNode.cost.totalAmount.amount),
      ),
    [lineNode],
  );

  /** 카트 아이템 제거 */
  const removeCartLine = (id: string) => {
    void removeCartLineBase({ variables: { cartId: cart.id, lineIds: [id] } });
  };

  const { isSale, salePercent, membershipDiscountAmount, codeDiscountAmount } = getDiscountPriceInfo(lineNode);

  /** 카트 아이템 수량 업데이트 */
  const handleChangeQuantity = async (num: number) => {
    if (updateCartLoading) return; // 변경중일 때

    // 1. 상품에 구매 수량제한이 존재하는 경우
    const quantityLimitProduct = parseInt(lineNode.merchandise.product.quantityLimit?.value || "-1");
    if (quantityLimitProduct > 0) {
      // 카트에 해당 상품이 담겨있는지 확인
      const cartLineItems = cart.lines.edges.filter(
        ({ node }) => lineNode.merchandise.product.id === node.merchandise.product.id,
      );
      // 카트에 담긴 상품의 수량
      const cartLineItemCount = cartLineItems.reduce((acc, curr) => acc + curr.node.quantity, 0);

      // 구매 내역에 해당 상품이 있는지 확인
      // exclude_limit이 true 인 경우 제외
      const itemInOrderHistory = customer.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => node.exclude_limit?.value !== "true" && !node.cancelReason)
        .filter(({ node }) => node.isfamilysale)
        .map(({ node }) => node.lineItems.edges)
        .flat()
        .filter(({ node }) => node.variant?.product.id === lineNode.merchandise.product.id);

      // TODO: currentQuantity: 환불되거나 제거된 제품을 제외한 실제로 주문된 수량 -> 실제로 주문한 개수를 파악하고 싶으면 이걸 확인해야한다고 생각함
      // https://shopify.dev/docs/api/admin-graphql/unstable/objects/LineItem#field-currentquantity
      const totalOrderedProductCount = itemInOrderHistory.reduce((total, { node }) => {
        return total + (node.quantity || 0); // currentQuantity가 0이거나 undefined일 경우를 대비해 0으로 처리
      }, 0);

      /**
       * 1. 사용자가 추가하려는 상품 수량(num)이 quantityLimitProduct을 초과하는 경우
       * 2. 카트에 담긴 상품 수량과 구매 내역을 합친 수량이 quantityLimitProduct을 초과하는 경우
       *    - lineNode.quantity: 장바구니에 이미 담긴 해당 상품의 수량
       *    - cartLineItemCount: 카트에 담긴 해당 상품의 총 수량
       *    - totalOrderedProductCount: 구매 내역에 포함된 해당 상품의 개수
       */
      if (
        num > quantityLimitProduct ||
        (lineNode.quantity <= num && cartLineItemCount + totalOrderedProductCount >= quantityLimitProduct)
      ) {
        openToast(NOTIFICATIONS.ERROR_EXCEED_QTY);
        return;
      }
    }

    // 2. 현재 고른 variant가 카트에 이미 담겨있는지 확인하고 이미 담겨있다면 구매 가능한 재고와 이미 담겨있는 재고를 비교
    if (num > (lineNode.merchandise.quantityAvailable || 99)) {
      openToast(NOTIFICATIONS.ERROR_EXCEED_QTY);
      return;
    }

    await updateCartLineBase({ variables: { cartId: cart.id, lines: { id: lineNode.id, quantity: num } } });
  };

  const hasMatchingDiscountCode = lineNode.attributes
    .map(
      (attr) =>
        attr.key === "discount_code" && // "discount_code" 키만 필터링
        lineNode.discountAllocations.some(
          (discountAllocation) =>
            discountAllocation.__typename === "CartCodeDiscountAllocation" &&
            discountAllocation.code.toLowerCase() === attr.value?.toLowerCase(), // code가 일치하는지 비교
        ),
    )
    .some((value) => value);

  return (
    <div className="flex justify-between gap-5">
      <div className="grow">
        <div className="mb-2.5">
          <h2 className="uppercase">
            <Link
              onClick={() => {
                closeCart();
              }}
              to={`${URLS.PRODUCTS}/${lineNode.merchandise.product.handle}`}
            >
              {lineNode.merchandise.product.vendor} {lineNode.merchandise.product.title}
            </Link>
          </h2>
          {!lineNode.merchandise.product.title.includes("Draft Order") && <span>Size {size?.value || ""}</span>}

          <div>
            <CompareAtPrice originalPrice={compareAtPrice} salePrice={price} />

            {/* 직원 할인 */}
            <div className="flex items-center gap-1 pt-1">
              {isEmployee && lineNode.discountAllocations.length
                ? lineNode.discountAllocations.map((discount, i) => (
                    <div className="flex items-center gap-1 pt-1" key={i}>
                      <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                      <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                        <span className="text-tuneGrey-400 text-sm">{getDiscountName(discount)}</span>
                        <span>{`(-${transformWon(compareAtPrice - price)}${t("account.order.currency")})`}</span>
                      </div>
                    </div>
                  ))
                : null}
            </div>

            {/* 할인 레이블 */}
            <div className="flex flex-col items-start">
              {/* 상품 할인 */}
              {isSale && !lineNode.discountAllocations.length ? (
                <div className="flex items-center gap-1 pt-1">
                  <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                  <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                    <span>{`${t("account.order.discount_product")} ${salePercent}%`}</span>
                    <span>{`(-${transformWon(compareAtPrice - price)}${t("account.order.currency")})`}</span>
                  </div>
                </div>
              ) : null}

              {/* 멤버십 할인 */}
              {/* 중복할인이 적용되는 과정에서 잠깐 `할인코드 할인률`만 노출됨 */}
              {!isEmployee && lineNode.discountAllocations.length
                ? lineNode.discountAllocations.map((discount, i) => (
                    <div className="flex items-center gap-1 pt-1" key={i}>
                      <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                      <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                        <span className="text-tuneGrey-400 text-sm">{getDiscountName(discount)}</span>
                        <span>
                          {/* eslint-disable-next-line no-nested-ternary */}
                          {discount.__typename === "CartAutomaticDiscountAllocation"
                            ? // 멤버십 할인 (title이 있는 경우)
                              `(-${transformWon(membershipDiscountAmount * lineNode.quantity)}${t("account.order.currency")})`
                            : discount.__typename === "CartCodeDiscountAllocation"
                              ? // 할인 코드 할인 (code가 있는 경우)
                                `(-${transformWon(codeDiscountAmount)}${t("account.order.currency")})`
                              : null}
                        </span>
                      </div>
                    </div>
                  ))
                : null}

              {/* 중복할인 */}
              {!isEmployee && lineNode.discountAllocations.length && lineNode.attributes.length ? (
                <>
                  {/*TODO: 카트에서는 할인코드 적용안된 상태임*/}
                  {/* 할인코드 할인 */}
                  {lineNode.attributes.length && !hasMatchingDiscountCode ? (
                    <div className="flex flex-col items-end gap-1">
                      {lineNode.attributes.map((attr, index) =>
                        attr.key === "discount_code" ? (
                          <div className="flex items-center gap-1" key={index}>
                            <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                            <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                              <span className="uppercase" key={attr.key}>
                                {attr.value}
                              </span>
                              <span>{`(-${transformWon(codeDiscountAmount * lineNode.quantity)}${t("account.order.currency")})`}</span>
                            </div>
                          </div>
                        ) : null,
                      )}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div>
          <span className="text-tuneGrey-400 mb-1 block text-sm">{t("cart.amount")}</span>
          <div className="flex gap-1">
            <Stepper
              handleChange={handleChangeQuantity}
              quantity={lineNode.quantity}
              quantityAvailable={lineNode.merchandise.quantityAvailable || 99}
            />
            <div>
              <Button
                className="pc:h-7.5 h-9"
                onClick={() => {
                  removeCartLine(lineNode.id);
                }}
                styleType="secondary"
                variant="containedIcon"
              >
                <Icon alt="trash_100" name={IconName.Trash_100} />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="aspect-4/5 w-full max-w-30 overflow-hidden rounded-sm bg-[#707070]/10">
        <Link
          onClick={() => {
            closeCart();
          }}
          to={`${URLS.PRODUCTS}/${lineNode.merchandise.product.handle}`}
        >
          <Image alt={lineNode.merchandise.product.title} src={lineNode.merchandise.image?.url} />
        </Link>
      </div>
    </div>
  );
}

export default CartLineItem;
